import { baseApi } from "./baseApi";
import { supabase } from "../lib/supabaseClient";

export const feedApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSocialFeed: builder.query<
      { activities: any[]; parties: any[]; suggestions: any[] },
      string // userId
    >({
      queryFn: async (userId) => {
        try {
          // 1. Get following IDs
          const { data: following } = await supabase
            .from("user_follows")
            .select("following_id")
            .eq("follower_id", userId);

          const followingIds = following?.map((f) => f.following_id) || [];
          let suggestions: any[] = [];

          // If not following anyone, fetch suggestions
          if (followingIds.length === 0) {
            const { data: sugg } = await supabase
              .from("user_profiles")
              .select("*")
              .neq("id", userId)
              .limit(5);
            suggestions = sugg || [];
          }

          // 2. Fetch Activities (Batching)
          const [currentRes, interactionsRes] = await Promise.all([
            supabase
              .from("user_current_activities")
              .select("user_id, imdb_id, started_at")
              .in("user_id", followingIds)
              .order("started_at", { ascending: false })
              .limit(10),
            supabase
              .from("user_interactions")
              .select("user_id, imdb_id, status, created_at")
              .in("user_id", followingIds)
              .order("created_at", { ascending: false })
              .limit(20),
          ]);

          const rawActivities = [
            ...(currentRes.data || []).map((a) => ({ ...a, type: "currently_watching", created_at: a.started_at })),
            ...(interactionsRes.data || []).map((a) => ({ ...a, type: a.status })),
          ];

          // 3. Collect IDs for batch fetching
          const userIds = Array.from(new Set(rawActivities.map((a) => a.user_id)));
          const imdbIds = Array.from(new Set(rawActivities.map((a) => a.imdb_id)));

          let activities: any[] = [];

          if (userIds.length > 0 && imdbIds.length > 0) {
            // 4. Batch fetch details
            const [usersRes, filmsRes] = await Promise.all([
              supabase.from("user_profiles").select("id, username, display_name, avatar_url").in("id", userIds),
              supabase.from("films").select("*").in("imdb_id", imdbIds),
            ]);

            const userMap = new Map(usersRes.data?.map((u) => [u.id, u]));
            const filmMap = new Map(filmsRes.data?.map((f) => [f.imdb_id, f]));

            // 5. Assemble
            activities = rawActivities
              .map((a) => {
                const user = userMap.get(a.user_id);
                const film = filmMap.get(a.imdb_id);
                if (!user || !film) return null;
                return {
                  id: `${a.type}-${a.user_id}-${a.imdb_id}-${a.created_at}`,
                  user,
                  film,
                  activity_type: a.type as any,
                  created_at: a.created_at,
                };
              })
              .filter((a) => a !== null)
              .sort((a, b) => new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime());
          }

          // 6. Fetch Active Parties
          const { data: parties } = await supabase
            .from("watch_parties")
            .select("*")
            .eq("status", "upcoming")
            .gt("scheduled_time", new Date().toISOString())
            .order("scheduled_time", { ascending: true })
            .limit(5);

          return {
            data: {
              activities,
              parties: parties || [],
              suggestions,
            },
          };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
      providesTags: ["Feed"],
      keepUnusedDataFor: 120, // Cache for 2 minutes
    }),
  }),
});

export const { useGetSocialFeedQuery, useGetSocialFeedQuery: useGetFeedQuery } = feedApi;
